import { ChangeEvent, useCallback, useState } from 'react';
import { useIsNative } from '~/context/common/DeviceContext';
import { Button as StyledButton, Input } from '../PassengerTools.styles';
import {
  CreatedUser,
  copyToClipboard,
  storeTestUserInLocalStorage
} from '../utils/createTestUser';

type OwnProps = {
  users: CreatedUser[];
  onBack: () => void;
  onClear?: () => void;
  onLogin: (user: CreatedUser) => Promise<void>;
  onUpdateUser: (user: CreatedUser) => void;
  title: string;
  showClearButton?: boolean;
};

export default function UsersList({
  users,
  onBack,
  onClear,
  onLogin,
  onUpdateUser,
  title,
  showClearButton = false
}: OwnProps) {
  const [editingUserEmail, setEditingUserEmail] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const isNative = useIsNative();

  const handleSaveName = useCallback(
    (user: CreatedUser) => {
      const nameToSave = newName.trim() || undefined;

      if (nameToSave !== user.name) {
        const updatedUser = { ...user, name: nameToSave };
        storeTestUserInLocalStorage(updatedUser);
        onUpdateUser(updatedUser);
      }
      setEditingUserEmail(null);
      setNewName('');
    },
    [newName, onUpdateUser]
  );

  return (
    <div className="flex flex-col gap-16">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        {showClearButton && users.length > 0 && onClear && (
          <button
            onClick={onClear}
            className="rounded-xs bg-red-500 px-8 py-4 text-xs text-white hover:bg-red-600"
            data-analytics-id={null}
            data-testid="clear-all-users"
          >
            Clear All Users
          </button>
        )}
      </div>

      {users.length === 0 ? (
        <div className="py-16 text-center text-gray-300">
          <p>No users found in local storage.</p>
          <p className="mt-8 text-sm">
            Create some users first using the Users tab.
          </p>
        </div>
      ) : (
        <div className="max-h-[400px] overflow-y-auto">
          <p className="mb-16 text-sm text-gray-300">
            Found {users.length.toLocaleString()} user
            {users.length !== 1 ? 's' : ''}{' '}
            {showClearButton ? 'in local storage' : 'created'}:
          </p>
          {users.map((user, index) => (
            <div
              key={`${user.email}-${index}`}
              className="mb-16 border-b border-gray-600 pb-16 last:border-b-0"
            >
              {editingUserEmail === user.email ? (
                <div className="mb-8 flex items-center gap-8">
                  <Input
                    value={newName}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setNewName(e.target.value)
                    }
                    placeholder="Enter name"
                    autoFocus
                  />
                  <button
                    onClick={() => handleSaveName(user)}
                    className="rounded-xs bg-blue-500 px-8 py-4 text-xs text-white hover:bg-blue-600"
                    data-analytics-id={null}
                    data-testid="save-user-name"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingUserEmail(null);
                      setNewName('');
                    }}
                    className="rounded-xs bg-gray-600 px-8 py-4 text-xs text-white hover:bg-gray-700"
                    data-analytics-id={null}
                    data-testid="cancel-edit-name"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="mb-8 flex items-center justify-between text-sm text-white">
                  <div>
                    {user.name && (
                      <div className="mb-4 text-lg font-bold text-blue-300">
                        {user.name}
                      </div>
                    )}
                    <strong>Type:</strong> {user.type}
                  </div>
                  <button
                    onClick={() => {
                      setEditingUserEmail(user.email);
                      setNewName(user.name || '');
                    }}
                    className="ml-8 rounded-xs bg-blue-500 px-8 py-4 text-xs text-white hover:bg-blue-600"
                    data-analytics-id={null}
                    data-testid="edit-user-name"
                  >
                    {user.name ? 'Rename' : 'Add Name'}
                  </button>
                </div>
              )}

              {user.hasLoanAmount ? (
                <div className="text-center">
                  <div className="mb-8 text-sm text-white">
                    <strong>
                      Trade with loan amount created successfully!
                    </strong>
                  </div>
                  <div className="mb-8 text-xs text-gray-300">
                    Refresh the page to see the trade information.
                  </div>
                  <div className="flex justify-center">
                    <button
                      onClick={() => window.location.reload()}
                      className="rounded-xs bg-orange-500 px-16 py-8 text-sm text-white hover:bg-orange-600"
                      data-analytics-id={null}
                      title="Refresh page to see trade with loan amount"
                      data-testid="refresh-page"
                    >
                      Refresh Page
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-8 flex items-center gap-8">
                    <span className="flex-1 text-sm text-white">
                      <strong>Email:</strong> {user.email}
                    </span>
                    <button
                      onClick={() => copyToClipboard(user.email)}
                      className="rounded-xs bg-blue-500 px-8 py-4 text-xs text-white hover:bg-blue-600"
                      data-analytics-id={null}
                      title="Copy email"
                      data-testid="copy-email"
                    >
                      Copy
                    </button>
                  </div>

                  <div className="mb-8 flex items-center gap-8">
                    <span className="flex-1 text-sm text-white">
                      <strong>Password:</strong> {user.password}
                    </span>
                    <button
                      onClick={() => copyToClipboard(user.password)}
                      className="rounded-xs bg-blue-500 px-8 py-4 text-xs text-white hover:bg-blue-600"
                      data-analytics-id={null}
                      title="Copy password"
                      data-testid="copy-password"
                    >
                      Copy
                    </button>
                  </div>

                  {user.ssn && (
                    <div className="mb-8 flex items-center gap-8">
                      <span className="flex-1 text-sm text-white">
                        <strong>SSN:</strong> {user.ssn}
                      </span>
                      <button
                        onClick={() => copyToClipboard(user.ssn!)}
                        className="rounded-xs bg-blue-500 px-8 py-4 text-xs text-white hover:bg-blue-600"
                        data-analytics-id={null}
                        title="Copy SSN"
                        data-testid="copy-ssn"
                      >
                        Copy
                      </button>
                    </div>
                  )}

                  {user.address && (
                    <div className="mb-8 flex items-center gap-8">
                      <span className="flex-1 text-sm text-white">
                        <strong>Address:</strong> {user.address.street},{' '}
                        {user.address.city}, {user.address.state}{' '}
                        {user.address.zip}
                      </span>
                      <button
                        onClick={() =>
                          copyToClipboard(
                            `${user.address!.street}, ${user.address!.city}, ${
                              user.address!.state
                            } ${user.address!.zip}`
                          )
                        }
                        className="rounded-xs bg-blue-500 px-8 py-4 text-xs text-white hover:bg-blue-600"
                        data-analytics-id={null}
                        title="Copy address"
                        data-testid="copy-address"
                      >
                        Copy
                      </button>
                    </div>
                  )}

                  {user.dateOfBirth && (
                    <div className="mb-8 flex items-center gap-8">
                      <span className="flex-1 text-sm text-white">
                        <strong>Date of Birth:</strong> {user.dateOfBirth}
                      </span>
                      <button
                        onClick={() => copyToClipboard(user.dateOfBirth!)}
                        className="rounded-xs bg-blue-500 px-8 py-4 text-xs text-white hover:bg-blue-600"
                        data-analytics-id={null}
                        title="Copy date of birth"
                        data-testid="copy-date-of-birth"
                      >
                        Copy
                      </button>
                    </div>
                  )}

                  {user.createdAt && showClearButton && (
                    <div className="mb-8 text-xs text-gray-400">
                      <strong>Created:</strong>{' '}
                      {new Date(user.createdAt).toLocaleString()}
                    </div>
                  )}

                  {!isNative && (
                    <div className="flex justify-start">
                      <button
                        onClick={() => onLogin(user)}
                        className="rounded-xs bg-green-500 px-16 py-8 text-sm text-white hover:bg-green-600"
                        data-analytics-id={null}
                        title="Login as this user"
                        data-testid="login-as-user"
                      >
                        Login as User
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end">
        <StyledButton
          type="button"
          onClick={onBack}
          data-analytics-id={null}
          data-testid="back-button"
        >
          {showClearButton ? 'Back to Create' : 'Back'}
        </StyledButton>
      </div>
    </div>
  );
}
